// Maths-only Babylon modules: the bake worker imports this file (see sky-bake.ts).
import { Color3 } from "@babylonjs/core/Maths/math.color.pure.js";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.pure.js";
import { smoothstep } from "@tiny-seas/shared";

/** Linear daylight radiance shared by visible sky, water, fog and indirect lighting. */
export const DAY_SKY_ZENITH = new Color3(0.025, 0.25, 0.55);
export const DAY_SKY_HORIZON = new Color3(0.24, 0.57, 0.70);
/** Direction daylight travels (from the sun towards the ground). */
export const DAY_SUN_DIRECTION = new Vector3(0.35, -0.55, 0.65).normalize();

/**
 * Shared sky/IBL field: rounded 3D cumulus with a flat shaded base, not a 2D mask. Needs uniforms
 * `sunDir`, `zenith`, `horizon`, `time` and `storm` (0 fair … 1 full storm: every cell clouds over,
 * groups swell, an overcast deck fills the gaps, and the clouds turn thick and grey).
 */
export function atmosphereGLSL(steps: 48 | 96): string {
  return `
float cloudHash(vec3 p) {
  vec3 q = fract(p * vec3(0.1031, 0.1030, 0.0973));
  q += dot(q, q.yxz + 33.33);
  return fract((q.x + q.y) * q.z);
}
float cloudNoise(vec3 p) {
  vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(cloudHash(i), cloudHash(i + vec3(1,0,0)), f.x),
                 mix(cloudHash(i + vec3(0,1,0)), cloudHash(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(cloudHash(i + vec3(0,0,1)), cloudHash(i + vec3(1,0,1)), f.x),
                 mix(cloudHash(i + vec3(0,1,1)), cloudHash(i + vec3(1,1,1)), f.x), f.y), f.z);
}
float cloudDensity(vec3 p) {
  float layer = smoothstep(180.0, 202.0, p.y) * (1.0 - smoothstep(340.0, 365.0, p.y));
  if (layer == 0.0) return 0.0;
  p.xz += vec2(time * 0.36, time * 0.13);
  vec2 cell = floor(p.xz / 620.0);
  float shape = 10.0;
  // Sparse, jittered groups of overlapping ellipsoids leave actual blue-sky
  // gaps. Density noise only erodes their edges, rather than filling the sky.
  for (int z = -1; z <= 1; z++) {
    for (int x = -1; x <= 1; x++) {
      vec3 seed = vec3(cell.x + float(x), 7.0, cell.y + float(z));
      float group = cloudHash(seed);
      if (group < 0.38 * (1.0 - storm)) continue;
      vec2 center = (seed.xz + 0.5 + vec2(cloudHash(seed + 1.0), cloudHash(seed + 9.0)) * 0.26 - 0.13) * 620.0;
      float radius = (125.0 + cloudHash(seed + 19.0) * 65.0) * (1.0 + 0.45 * storm);
      vec3 q = vec3(p.x - center.x, p.y - 239.0, p.z - center.y);
      if (abs(q.x) > radius * 1.85 || abs(q.z) > radius * 1.6) continue;
      float a = length(q / vec3(radius, 86.0, radius * 0.90)) - 1.0;
      float b = length((q - vec3(-radius * 0.62, 24.0, radius * 0.15)) / vec3(radius * 0.72, 77.0, radius * 0.75)) - 1.0;
      float c = length((q - vec3(radius * 0.60, 10.0, -radius * 0.24)) / vec3(radius * 0.77, 80.0, radius * 0.71)) - 1.0;
      shape = min(shape, min(a, min(b, c)));
    }
  }
  // The storm deck thickens out of the noise as storminess rises, filling the blue gaps; a finer
  // octave lumps its base and top so it reads as rolling cloud, not a flat lid.
  if (storm > 0.0) {
    float deck = abs(p.y - 236.0) / 52.0 - 1.0 + (cloudNoise(p / 260.0) - 0.5) * 0.8 + (cloudNoise(p / 70.0) - 0.5) * 0.35;
    shape = min(shape, deck + (1.0 - storm) * 1.2);
  }
  if (shape > 0.16) return 0.0;
  shape += (cloudNoise(p / 47.0) - 0.5) * 0.11;
  return (1.0 - smoothstep(-0.10, 0.10, shape)) * layer;
}
vec3 skyGradient(vec3 d) {
  float height = clamp(d.y, 0.0, 1.0);
  float blend = pow(height, 0.55) * smoothstep(0.0, 0.12, height);
  return mix(horizon, zenith, blend);
}
vec3 atmosphereRadiance(vec3 d) {
  vec3 col = skyGradient(d);
  float s = max(dot(d, sunDir), 0.0);
  float visible = smoothstep(0.0, 0.08, d.y);
  if (d.y > 0.025) {
    float start = 180.0 / d.y;
    float finish = min(365.0 / d.y, start + 4200.0);
    float stepLength = (finish - start) / ${steps.toFixed(1)};
    float jitter = 0.1 + cloudHash(floor(d * 8192.0)) * 0.8;
    float transmittance = 1.0;
    vec3 cloudLight = vec3(0.0);
    for (int i = 0; i < ${steps}; i++) {
      if (transmittance < 0.015) break;
      vec3 p = d * (start + (float(i) + jitter) * stepLength);
      float density = cloudDensity(p);
      float opacity = 1.0 - exp(-density * stepLength * 0.024 * (1.0 + storm));
      if (opacity == 0.0) continue;
      float shade = exp(-(cloudDensity(p + sunDir * 38.0) + cloudDensity(p + sunDir * 85.0)) * 1.65);
      float top = smoothstep(195.0, 320.0, p.y);
      // Storm grey arrives ahead of full cover, so a building storm already looks heavy.
      float grey = min(1.0, storm * 1.6);
      vec3 light = mix(mix(vec3(0.48, 0.60, 0.74), vec3(0.10, 0.11, 0.13), grey), mix(vec3(1.25, 1.18, 1.03), vec3(0.55, 0.58, 0.62), grey), shade * 0.78 + top * 0.22);
      light += vec3(0.25, 0.22, 0.16) * pow(s, 12.0) * shade * (1.0 - storm);
      // Darker and lighter patches drift through the deck.
      if (storm > 0.0) light *= mix(1.0, 0.6 + 0.8 * cloudNoise(p / 150.0 + vec3(time * 0.03, 0.0, 0.0)), storm);
      float haze = exp(-length(p.xz) * 0.00012);
      light = mix(horizon, light, haze);
      cloudLight += light * opacity * transmittance;
      transmittance *= 1.0 - opacity;
    }
    float cloudVisibility = smoothstep(0.025, 0.11, d.y);
    col = mix(col, col * transmittance + cloudLight, cloudVisibility);
    // Fine cirrus lies above the rounded masses, soft enough not to look like smears.
    vec2 wisps = d.xz / (d.y + 0.12);
    float filament = cloudNoise(vec3(wisps.x * 3.0, 19.0, wisps.y * 20.0));
    float cirrus = smoothstep(0.70, 0.90, filament) * 0.10 * smoothstep(0.10, 0.35, d.y) * (1.0 - storm);
    col = mix(col, vec3(0.90, 0.94, 0.98), cirrus);
    visible *= transmittance * (1.0 - 0.85 * storm);
  }
  float halo = pow(s, 32.0) * 0.22 + pow(s, 320.0) * 0.65;
  float disk = smoothstep(0.99978, 0.99994, s);
  return col + visible * (vec3(1.0, 0.86, 0.65) * halo + vec3(6.0, 5.2, 4.0) * disk);
}`;
}

function hash(x: number, y: number, z: number): number {
  let px = x * 0.1031, py = y * 0.1030, pz = z * 0.0973;
  px -= Math.floor(px); py -= Math.floor(py); pz -= Math.floor(pz);
  const dot = px * (py + 33.33) + py * (px + 33.33) + pz * (pz + 33.33);
  px += dot; py += dot; pz += dot;
  const n = (px + py) * pz;
  return n - Math.floor(n);
}
function noise(x: number, y: number, z: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  let fx = x - ix, fy = y - iy, fz = z - iz;
  fx *= fx * (3 - 2 * fx); fy *= fy * (3 - 2 * fy); fz *= fz * (3 - 2 * fz);
  const a = hash(ix, iy, iz) * (1 - fx) + hash(ix + 1, iy, iz) * fx;
  const b = hash(ix, iy + 1, iz) * (1 - fx) + hash(ix + 1, iy + 1, iz) * fx;
  const c = hash(ix, iy, iz + 1) * (1 - fx) + hash(ix + 1, iy, iz + 1) * fx;
  const d = hash(ix, iy + 1, iz + 1) * (1 - fx) + hash(ix + 1, iy + 1, iz + 1) * fx;
  return (a * (1 - fy) + b * fy) * (1 - fz) + (c * (1 - fy) + d * fy) * fz;
}
function density(x: number, y: number, z: number): number {
  const layer = smoothstep(180, 202, y) * (1 - smoothstep(340, 365, y));
  if (layer === 0) return 0;
  const cellX = Math.floor(x / 620), cellZ = Math.floor(z / 620);
  let shape = 10;
  for (let iz = -1; iz <= 1; iz++) {
    for (let ix = -1; ix <= 1; ix++) {
      const sx = cellX + ix, sz = cellZ + iz;
      if (hash(sx, 7, sz) < 0.38) continue;
      const cx = (sx + 0.5 + hash(sx + 1, 8, sz + 1) * 0.26 - 0.13) * 620;
      const cz = (sz + 0.5 + hash(sx + 9, 16, sz + 9) * 0.26 - 0.13) * 620;
      const radius = 125 + hash(sx + 19, 26, sz + 19) * 65;
      const qx = x - cx, qy = y - 239, qz = z - cz;
      if (Math.abs(qx) > radius * 1.85 || Math.abs(qz) > radius * 1.6) continue;
      const a = Math.hypot(qx / radius, qy / 86, qz / (radius * 0.90)) - 1;
      const b = Math.hypot((qx + radius * 0.62) / (radius * 0.72), (qy - 24) / 77, (qz - radius * 0.15) / (radius * 0.75)) - 1;
      const c = Math.hypot((qx - radius * 0.60) / (radius * 0.77), (qy - 10) / 80, (qz + radius * 0.24) / (radius * 0.71)) - 1;
      shape = Math.min(shape, a, b, c);
    }
  }
  if (shape > 0.16) return 0;
  shape += (noise(x / 47, y / 47, z / 47) - 0.5) * 0.11;
  return (1 - smoothstep(-0.10, 0.10, shape)) * layer;
}

/** `s` is non-negative; squaring retains the daylight bake's integer-power fast path. */
function pow32(s: number): number {
  const s2 = s * s;
  const s4 = s2 * s2;
  const s8 = s4 * s4;
  const s16 = s8 * s8;
  return s16 * s16;
}

/** s^320 as (s^32)^10. */
function pow320(s: number): number {
  const p = pow32(s);
  const p2 = p * p;
  const p4 = p2 * p2;
  const p5 = p4 * p;
  return p5 * p5;
}

/** Same linear radiance as the visible shader at time zero, for static indirect light. */
export function daylightRadiance(x: number, y: number, z: number, zenith: Color3, horizon: Color3, sun: Vector3, out: Color3): void {
  if (y <= 0) {
    out.set(horizon.r, horizon.g, horizon.b);
    return;
  }
  const h = Math.max(0, Math.min(1, y));
  const t = Math.pow(h, 0.55) * smoothstep(0, 0.12, h);
  const s = Math.max(0, x * sun.x + y * sun.y + z * sun.z);
  let visible = smoothstep(0, 0.08, y);
  let r = horizon.r + (zenith.r - horizon.r) * t;
  let g = horizon.g + (zenith.g - horizon.g) * t;
  let b = horizon.b + (zenith.b - horizon.b) * t;
  if (y > 0.025) {
    const start = 180 / y;
    const step = (Math.min(365 / y, start + 4200) - start) / 96;
    const jitter = 0.1 + hash(Math.floor(x * 8192), Math.floor(y * 8192), Math.floor(z * 8192)) * 0.8;
    let transmittance = 1, cr = 0, cg = 0, cb = 0;
    const sunGlow = Math.pow(s, 12);
    for (let i = 0; i < 96; i++) {
      if (transmittance < 0.015) break;
      const distance = start + (i + jitter) * step;
      const px = x * distance, py = y * distance, pz = z * distance;
      const opacity = 1 - Math.exp(-density(px, py, pz) * step * 0.024);
      if (opacity === 0) continue;
      const shade = Math.exp(-(density(px + sun.x * 38, py + sun.y * 38, pz + sun.z * 38)
        + density(px + sun.x * 85, py + sun.y * 85, pz + sun.z * 85)) * 1.65);
      const lit = shade * 0.78 + smoothstep(195, 320, py) * 0.22;
      const glow = sunGlow * shade;
      const haze = Math.exp(-Math.hypot(px, pz) * 0.00012);
      const weight = opacity * transmittance;
      cr += (horizon.r + (0.48 + 0.77 * lit + 0.25 * glow - horizon.r) * haze) * weight;
      cg += (horizon.g + (0.60 + 0.58 * lit + 0.22 * glow - horizon.g) * haze) * weight;
      cb += (horizon.b + (0.74 + 0.29 * lit + 0.16 * glow - horizon.b) * haze) * weight;
      transmittance *= 1 - opacity;
    }
    const cloudVisibility = smoothstep(0.025, 0.11, y);
    r += (r * transmittance + cr - r) * cloudVisibility;
    g += (g * transmittance + cg - g) * cloudVisibility;
    b += (b * transmittance + cb - b) * cloudVisibility;
    const cirrus = smoothstep(0.70, 0.90, noise(x / (y + 0.12) * 3, 19, z / (y + 0.12) * 20))
      * 0.10 * smoothstep(0.10, 0.35, y);
    r += (0.90 - r) * cirrus; g += (0.94 - g) * cirrus; b += (0.98 - b) * cirrus;
    visible *= transmittance;
  }
  const glow = (pow32(s) * 0.22 + pow320(s) * 0.65) * visible;
  const disc = smoothstep(0.99978, 0.99994, s) * visible;
  out.set(r + glow + 6 * disc, g + glow * 0.86 + 5.2 * disc, b + glow * 0.65 + 4 * disc);
}
